/**
 * Silence-detecting VAD: an AnalyserNode off an existing MediaStream, RMS
 * sampled on an interval. Calibrates against ~300ms of ambient noise on
 * start() rather than using a hardcoded absolute level — rural/office/home
 * ambient noise floors vary enormously and a fixed threshold would fail in
 * most of them.
 */

export interface VadCallbacks {
  /** Fires once when RMS has stayed above threshold for speechConfirmMs. */
  onSpeechStart?: () => void;
  /** Fires once when RMS has stayed below threshold for silenceEndMs after
   * speech was active — i.e. "the turn just ended". */
  onTurnEnd?: () => void;
  /** Fires on every sample, for a live input-level UI indicator. */
  onLevel?: (rms: number) => void;
}

export interface VadOptions {
  sampleIntervalMs?: number;
  calibrationMs?: number;
  speechConfirmMs?: number;
  silenceEndMs?: number;
  /** Speech threshold = max(ambientRms * thresholdMultiplier, minThreshold). */
  thresholdMultiplier?: number;
  minThreshold?: number;
}

const DEFAULTS: Required<VadOptions> = {
  sampleIntervalMs: 50,
  calibrationMs: 300,
  speechConfirmMs: 150,
  silenceEndMs: 1200,
  thresholdMultiplier: 2.5,
  minThreshold: 0.01,
};

export class VoiceActivityDetector {
  private analyser: AnalyserNode;
  private source: MediaStreamAudioSourceNode;
  private dataArray: Float32Array;
  private intervalId: number | null = null;
  private opts: Required<VadOptions>;
  private callbacks: VadCallbacks;

  private threshold = 0;
  private calibrating = false;
  private calibrationSamples: number[] = [];
  private calibrationStartMs = 0;

  private speechActive = false;
  private aboveSinceMs: number | null = null;
  private belowSinceMs: number | null = null;

  constructor(
    audioContext: AudioContext,
    stream: MediaStream,
    opts?: VadOptions,
    callbacks?: VadCallbacks
  ) {
    this.opts = { ...DEFAULTS, ...opts };
    this.callbacks = callbacks || {};
    this.analyser = audioContext.createAnalyser();
    this.analyser.fftSize = 1024;
    this.source = audioContext.createMediaStreamSource(stream);
    this.source.connect(this.analyser);
    this.dataArray = new Float32Array(this.analyser.fftSize);
  }

  private computeRms(): number {
    this.analyser.getFloatTimeDomainData(this.dataArray);
    let sumSquares = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      sumSquares += this.dataArray[i] * this.dataArray[i];
    }
    return Math.sqrt(sumSquares / this.dataArray.length);
  }

  /** Begin calibration then continuous detection. Safe to call again after stop(). */
  start(): void {
    if (this.intervalId !== null) return;
    this.calibrating = true;
    this.calibrationSamples = [];
    this.calibrationStartMs = performance.now();
    this.speechActive = false;
    this.aboveSinceMs = null;
    this.belowSinceMs = null;

    this.intervalId = window.setInterval(() => this.tick(), this.opts.sampleIntervalMs);
  }

  private tick(): void {
    const rms = this.computeRms();
    this.callbacks.onLevel?.(rms);

    if (this.calibrating) {
      this.calibrationSamples.push(rms);
      if (performance.now() - this.calibrationStartMs >= this.opts.calibrationMs) {
        const ambient =
          this.calibrationSamples.reduce((a, b) => a + b, 0) / this.calibrationSamples.length;
        this.threshold = Math.max(ambient * this.opts.thresholdMultiplier, this.opts.minThreshold);
        this.calibrating = false;
      }
      return;
    }

    const now = performance.now();
    if (rms >= this.threshold) {
      this.belowSinceMs = null;
      if (!this.speechActive) {
        if (this.aboveSinceMs === null) this.aboveSinceMs = now;
        if (now - this.aboveSinceMs >= this.opts.speechConfirmMs) {
          this.speechActive = true;
          this.callbacks.onSpeechStart?.();
        }
      }
    } else {
      this.aboveSinceMs = null;
      if (this.speechActive) {
        if (this.belowSinceMs === null) this.belowSinceMs = now;
        if (now - this.belowSinceMs >= this.opts.silenceEndMs) {
          this.speechActive = false;
          this.belowSinceMs = null;
          this.callbacks.onTurnEnd?.();
        }
      }
    }
  }

  /** Last-calibrated speech threshold — used by barge-in detection (Segment
   * V1.3) as a floor so a silent device can't produce an absurdly low bar. */
  getThreshold(): number {
    return this.threshold;
  }

  /** Resume sampling already in the "speech is happening" state, reusing the
   * last-calibrated threshold instead of recalibrating from scratch. For
   * barge-in (Segment V1.3): BargeInDetector has already confirmed speech
   * is happening by the time it fires, so recalibrating "ambient" against
   * live speech here would corrupt the threshold — this skips that and just
   * arms turn-end (onTurnEnd) detection directly. */
  resumeAsSpeaking(): void {
    if (this.intervalId === null) {
      this.intervalId = window.setInterval(() => this.tick(), this.opts.sampleIntervalMs);
    }
    this.calibrating = false;
    this.speechActive = true;
    this.aboveSinceMs = null;
    this.belowSinceMs = null;
  }

  /** Stop sampling. Does not disconnect the audio graph — call disconnect() for that. */
  stop(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /** Fully releases the AnalyserNode/source connection (call on IDLE/unmount). */
  disconnect(): void {
    this.stop();
    this.source.disconnect();
    this.analyser.disconnect();
  }
}
